import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  Package,
  Calendar,
  DollarSign,
  Eye,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Truck,
  Home,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import LightLoader from "../common/LightLoader";
import { SkeletonOrderDetail } from "../common/SkeletonLoader";
import { getOrder } from "../../config/api";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import {
  BsCheckCircle,
  BsTruck,
  BsCalendar,
  BsCreditCard,
  BsCash,
  BsReceipt,
  BsArrowLeft,
} from "react-icons/bs";
import { FaRupeeSign } from "react-icons/fa";
import { motion } from "framer-motion";
import { getImageUrl } from "../../utils/helpers";
import { openProductById } from "../../utils/productNavigation";

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      console.log("No user found, redirecting to login");
      navigate("/login");
      return;
    }
    console.log("User authenticated:", user.email);
    fetchOrder();
  }, [orderId, user]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching order with ID:", orderId);

      const response = await getOrder(orderId);
      console.log("Order response:", response);

      if (response.success && response.data) {
        console.log("📦 Order data received:", response.data);
        console.log("🔍 Order items:", response.data.order_items);
        setOrder(response.data);
      } else {
        console.error("Order not found or invalid response:", response);
        setError("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      paid: "bg-green-100 text-green-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
      refunded: "bg-orange-100 text-orange-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-poppins">
        <LightLoader size="lg" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-poppins">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 font-outfit">
            Order Not Found
          </h2>
          <p className="text-gray-600 mb-8 font-poppins">
            {error || "The order you're looking for doesn't exist."}
          </p>
          <button
            onClick={() => navigate("/shop")}
            className="bg-black text-white px-6 py-3 font-medium font-poppins hover:bg-gray-800 transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-poppins">
      <Helmet>
        <title>Order #{order.order_number} - Knots & Krafts</title>
        <meta
          name="description"
          content={`Order details for order #${order.order_number}`}
        />
      </Helmet>

      {/* Header */}
      <div className="bg-white border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 transition-colors"
              >
                <BsArrowLeft className="w-5 h-5 text-primary-600" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-outfit">
                  Order #{order.order_number}
                </h1>
                <p className="text-sm sm:text-base text-gray-600 font-poppins">
                  Placed on {new Date(order.created_at).toLocaleDateString()}
                </p>
                {order.order_token && (
                  <Link
                    to={`/track/${order.order_token}`}
                    className="text-sm text-amber-700 hover:text-amber-800 font-medium mt-1 inline-block"
                  >
                    View tracking page →
                  </Link>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <span
                className={`px-3 py-1 text-xs sm:text-sm font-medium font-poppins ${getStatusColor(
                  order.status
                )}`}
              >
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
              <span
                className={`px-3 py-1 text-xs sm:text-sm font-medium font-poppins ${getPaymentStatusColor(
                  order.payment_status
                )}`}
              >
                Payment{" "}
                {order.payment_status.charAt(0).toUpperCase() +
                  order.payment_status.slice(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Order Items */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <motion.div
              className="bg-white border p-4 sm:p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center font-outfit">
                <BsReceipt className="mr-2 text-primary-600" />
                Order Items
              </h2>

              <div className="space-y-3 sm:space-y-4">
                {order.order_items?.map((item) => (
                  <motion.div
                    key={item.id}
                    className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                    onClick={() =>
                      openProductById(item.product_id, item.variant_id, navigate)
                    }
                  >
                    {item.image ? (
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        className="w-12 h-16 sm:w-16 sm:h-16 object-cover flex-shrink-0 rounded-lg"
                        onError={(e) => {
                          console.error(
                            `❌ Image failed to load for ${item.name}:`,
                            item.image
                          );
                          e.target.src = "/placeholder.jpg";
                        }}
                        onLoad={() => {
                          console.log(
                            `✅ Image loaded successfully for ${item.name}:`,
                            getImageUrl(item.image)
                          );
                        }}
                      />
                    ) : (
                      <div className="w-12 h-16 sm:w-16 sm:h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-gray-500 font-medium text-center px-1">
                          {item.name
                            .split(" ")
                            .slice(0, 2)
                            .map((word) => word.charAt(0))
                            .join("")
                            .toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 hover:text-blue-600 transition-colors text-sm sm:text-base font-poppins">
                        {item.name}
                      </h3>
                      <p className="text-gray-600 text-sm font-poppins">
                        Quantity: {item.quantity}
                      </p>

                      {/* Variant Details */}
                      {item.variant_sku && (
                        <p className="text-sm text-blue-600 font-medium font-poppins">
                          SKU: {item.variant_sku}
                        </p>
                      )}

                      {/* Variant Attributes */}
                      {item.variant_attributes &&
                        Object.keys(item.variant_attributes).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.entries(item.variant_attributes).map(
                              ([key, value]) => (
                                <span
                                  key={key}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 font-poppins"
                                >
                                  {key}: {value}
                                </span>
                              )
                            )}
                          </div>
                        )}

                      <p className="text-xs text-gray-500 mt-1 hover:text-blue-600 transition-colors font-poppins">
                        Click to view product details →
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-gray-900 flex items-center text-sm sm:text-base font-poppins">
                        <FaRupeeSign className="w-3 h-3 mr-1" />
                        {(item.price * item.quantity).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600 font-poppins">
                        <FaRupeeSign className="w-2 h-2 mr-1 inline" />
                        {item.price.toFixed(2)} each
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Shipping Address */}
            <motion.div
              className="bg-white border p-4 sm:p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center font-outfit">
                <BsTruck className="mr-2 text-primary-600" />
                Shipping Address
              </h2>

              <div className="text-gray-700 font-poppins">
                <p className="font-medium">
                  {order.customer_details?.firstName}{" "}
                  {order.customer_details?.lastName}
                </p>
                <p className="text-sm sm:text-base">
                  {order.shipping_address?.address}
                </p>
                <p className="text-sm sm:text-base">
                  {order.shipping_address?.city},{" "}
                  {order.shipping_address?.postalCode}
                </p>
                <p className="text-sm sm:text-base">
                  {order.shipping_address?.country}
                </p>
                {order.customer_details?.phone && (
                  <p className="mt-2 text-sm sm:text-base">
                    Phone: {order.customer_details.phone}
                  </p>
                )}
                {order.customer_details?.email && (
                  <p className="text-sm sm:text-base">
                    Email: {order.customer_details.email}
                  </p>
                )}
              </div>
            </motion.div>

            {/* Shipping Tracking */}
            {order.status === "shipped" &&
              order.shipping_company &&
              order.tracking_number && (
                <motion.div
                  className="bg-white border p-4 sm:p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                >
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center font-outfit">
                    <BsTruck className="mr-2 text-purple-500" />
                    Shipping Information
                  </h2>

                  <div className="bg-purple-50 p-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600 font-poppins">
                          Shipping Company
                        </p>
                        <p className="font-medium text-gray-900 font-poppins">
                          {order.shipping_company}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600 font-poppins">
                          Tracking Number
                        </p>
                        <p className="font-mono text-purple-700 font-medium text-sm sm:text-base font-poppins">
                          {order.tracking_number}
                        </p>
                      </div>

                      {order.tracking_url && (
                        <div className="pt-2">
                          <a
                            href={order.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs sm:text-sm px-3 py-2 bg-black text-white font-medium font-poppins hover:bg-gray-800 transition-colors"
                          >
                            <BsTruck className="w-3 h-3 mr-1" />
                            Track Package
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

            {/* Order Timeline */}
            <motion.div
              className="bg-white border p-4 sm:p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center font-outfit">
                <BsCalendar className="mr-2 text-purple-500" />
                Order Timeline
              </h2>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm sm:text-base font-poppins">
                      Order Placed
                    </p>
                    <p className="text-sm text-gray-600 font-poppins">
                      {new Date(order.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {order.payment_status === "completed" && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm sm:text-base font-poppins">
                        Payment Confirmed
                      </p>
                      <p className="text-sm text-gray-600 font-poppins">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {order.status === "processing" && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm sm:text-base font-poppins">
                        Order Processing
                      </p>
                      <p className="text-sm text-gray-600 font-poppins">
                        Preparing your order
                      </p>
                    </div>
                  </div>
                )}

                {order.status === "shipped" && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-purple-500 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm sm:text-base font-poppins">
                        Order Shipped
                      </p>
                      <p className="text-sm text-gray-600 font-poppins">
                        {order.shipped_at
                          ? new Date(order.shipped_at).toLocaleString()
                          : "On its way to you"}
                      </p>
                    </div>
                  </div>
                )}

                {order.status === "delivered" && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm sm:text-base font-poppins">
                        Order Delivered
                      </p>
                      <p className="text-sm text-gray-600 font-poppins">
                        {order.delivered_at
                          ? new Date(order.delivered_at).toLocaleString()
                          : "Successfully delivered"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              className="bg-white border p-4 sm:p-6 lg:sticky lg:top-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-6 font-outfit">
                Order Summary
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between text-gray-600 text-sm sm:text-base font-poppins">
                  <span>Items Price</span>
                  <span className="flex items-center">
                    <FaRupeeSign className="w-3 h-3 mr-1" />
                    {order.items_price?.toFixed(2)}
                  </span>
                </div>

                {Number(order.discount_amount) > 0 && (
                  <div className="flex justify-between text-green-600 text-sm sm:text-base font-poppins">
                    <span>
                      Discount
                      {order.coupon_code ? ` (${order.coupon_code})` : ""}
                    </span>
                    <span className="flex items-center">
                      -<FaRupeeSign className="w-3 h-3 mr-1" />
                      {Number(order.discount_amount).toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-gray-600 text-sm sm:text-base font-poppins">
                  <span>Shipping</span>
                  <span className="flex items-center">
                    {order.shipping_price === 0 ? (
                      <span className="text-green-600 font-medium">FREE</span>
                    ) : (
                      <>
                        <FaRupeeSign className="w-3 h-3 mr-1" />
                        {order.shipping_price?.toFixed(2)}
                      </>
                    )}
                  </span>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg font-bold text-gray-900 font-outfit">
                    <span>Total</span>
                    <span className="flex items-center text-yellow-600">
                      <FaRupeeSign className="w-4 h-4 mr-1" />
                      {order.total_price?.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-center mt-2">
                    <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                      Inclusive of all taxes
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center font-outfit">
                  {order.payment_method === "Razorpay" ? (
                    <BsCreditCard className="mr-2 text-primary-600" />
                  ) : (
                    <BsCash className="mr-2 text-green-500" />
                  )}
                  Payment Method
                </h3>
                <p className="text-gray-600 text-sm sm:text-base font-poppins">
                  {order.payment_method === "Razorpay"
                    ? "Online Payment"
                    : "Cash on Delivery"}
                </p>
                {order.payment_info?.razorpay_payment_id && (
                  <p className="text-sm text-gray-500 mt-1 font-poppins">
                    Payment ID: {order.payment_info.razorpay_payment_id}
                  </p>
                )}
              </div>

              {/* Success Message */}
              {order.payment_status === "completed" && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200">
                  <div className="flex items-center">
                    <BsCheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p className="font-medium text-green-900 text-sm sm:text-base font-poppins">
                        Payment Successful
                      </p>
                      <p className="text-sm text-green-700 font-poppins">
                        Your payment has been processed successfully.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => navigate("/shop")}
                  className="w-full bg-black text-white py-2 px-4 font-medium font-poppins hover:bg-gray-800 transition-colors text-sm sm:text-base"
                >
                  Continue Shopping
                </button>
                <button
                  onClick={() => navigate("/orders")}
                  className="w-full bg-gray-100 text-gray-700 py-2 px-4 font-medium font-poppins hover:bg-gray-200 transition-colors text-sm sm:text-base"
                >
                  View All Orders
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
